import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react'

import { useCtaTracking } from '@lib/hooks/use-cta-tracking'
import { Cookie, setCookie } from '@lib/utils/cookies'
import { enableGoExtension } from '@lib/utils/extension'
import { getSessionTransferUrl } from '@lib/utils/session-transfer'

import { getConfig } from '@config/index'

import { ComponentCtaButtonType } from '@constants/enums/cta'

import { type CtaProps } from '@components/cta/cta-prop-types'
import { Button } from '@components/ui-core/button/button'

import { OnePasswordExtensionBypass } from '@custom/pages/superhuman-go-intro/main-content/go-time/fake-notch/one-password-extension-bypass'

import { useProductCta } from '../use-product-cta'

import { useExtensionAvailability } from './use-extension-availibility'

import styles from '../cta-button.module.scss'

const config = getConfig()

const GO_ENABLE_TIMEOUT_MS = 5000 // 5 seconds

export function GoCtaButton(props: CtaProps): ReactElement {
  const { isExtensionWithoutGo, isGoAvailable, getIsGoAvailable } = useExtensionAvailability()
  const {
    text,
    url: installUrl,
    underLinkText,
    dataAttributes,
  } = useProductCta(ComponentCtaButtonType.Go)
  const { isCompact, category = 'primary', ...restProps } = props

  const { trackAndNavigate, isLoading } = useCtaTracking({
    placement: 'Section',
    loadingTimeout: GO_ENABLE_TIMEOUT_MS,
  })

  const [goEnableFailed, setGoEnableFailed] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const onClickHandler = useCallback(async () => {
    // This cookie is used by Extension to enable Go experience post-install
    setCookie(Cookie.GoInstallationSource, 'superhuman')

    if (isExtensionWithoutGo && !goEnableFailed) {
      enableGoExtension()

      if (!timeoutRef.current) {
        // Set timer to check if extension was enabled after timeout
        timeoutRef.current = setTimeout(() => {
          const isGoAvailable = getIsGoAvailable()
          if (!isGoAvailable) {
            setGoEnableFailed(true)
          }
        }, GO_ENABLE_TIMEOUT_MS)
      }
    }

    trackAndNavigate(
      ComponentCtaButtonType.Go,
      text,
      category === 'unstyled' ? 'text' : category,
      installUrl,
    )
  }, [
    installUrl,
    text,
    category,
    isExtensionWithoutGo,
    goEnableFailed,
    getIsGoAvailable,
    trackAndNavigate,
  ])

  useEffect(() => {
    if (goEnableFailed) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      const sessionTransferUrl = getSessionTransferUrl(config.sessionTransfer.toGrammarly, {
        rpHost: config.domains.main,
        rpPath: config.paths.goIntro,
      })

      window.location.href = sessionTransferUrl
    }
  }, [goEnableFailed])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const dataAttrProps = (dataAttributes ?? []).reduce<Record<string, boolean>>((acc, attribute) => {
    acc[attribute] = true
    return acc
  }, {})

  return (
    <>
      <Button
        category={category}
        onClick={onClickHandler}
        isCompact={isCompact}
        isLoading={isLoading}
        href={installUrl}
        {...dataAttrProps}
        {...restProps}
        className={styles.goCtaButton}
      >
        {text}
        {isGoAvailable && <OnePasswordExtensionBypass />}
      </Button>
      {underLinkText && <p className={styles.ctaUnderLink}>{underLinkText}</p>}
    </>
  )
}
